// frontend/src/pages/model/DatasetHeader.jsx
// The selected dataset's name, where it comes from, and its certification. Admins
// rename it (saved when you leave the box; Enter saves, Escape cancels) and
// certify it; everyone else sees the same facts without controls.
import React from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, Loader2 } from 'lucide-react';
import Switch from './Switch';
import { MAX_LABEL, namePatch, profiledLabel, sourceName } from './modelLogic';

const muted = 'text-[var(--ui-text-muted)]';

/** dataset: the model's dataset; onChange(patch) saves a change; busy while it is being saved; error: text or ''. */
export default function DatasetHeader({ dataset, canEdit, busy = false, error = '', onChange }) {
  const name = dataset.display_name;
  const commit = (el) => {
    const patch = namePatch(name, el.value);
    if (patch) onChange(patch); else el.value = name;
  };
  const facts = [
    sourceName(dataset.source_key),
    dataset.entity,
    Number.isFinite(dataset.records_sampled) ? `${dataset.records_sampled.toLocaleString('en-US')} records sampled` : '',
    profiledLabel(dataset.last_profiled_at) ? `profiled ${profiledLabel(dataset.last_profiled_at)}` : '',
  ].filter(Boolean);

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0 flex-1">
          {canEdit ? (
            <input
              key={`${dataset.id}\u0000${name}\u0000${error ? 'failed' : ''}`}
              type="text" defaultValue={name} maxLength={MAX_LABEL} spellCheck={false} aria-label="Dataset name"
              title="Rename the dataset. Clear the box to use the automatic name."
              onBlur={(e) => commit(e.currentTarget)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur();
                if (e.key === 'Escape') { e.currentTarget.value = name; e.currentTarget.blur(); }
              }}
              className="-mx-2 h-9 w-[calc(100%+1rem)] rounded-lg bg-transparent px-2 text-lg font-semibold tracking-[-0.01em] text-[var(--ui-text)] hover:bg-[var(--ui-inset)] focus:bg-[var(--ui-inset)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
            />
          ) : (
            <h2 className="truncate text-lg font-semibold tracking-[-0.01em] text-[var(--ui-text)]">{name}</h2>
          )}
          <p className={`mt-0.5 text-xs ${muted}`}>{facts.join(' · ')}</p>
        </div>
        <div className="flex items-center gap-4">
          {busy && <Loader2 className={`h-4 w-4 animate-spin ${muted}`} role="status" aria-label="Saving" />}
          {canEdit ? (
            <label className="flex items-center gap-2 text-[13px] text-[var(--ui-text)]">
              <Switch checked={dataset.is_certified} disabled={busy} label="Certified" onChange={(next) => onChange({ is_certified: next })} />
              Certified
            </label>
          ) : dataset.is_certified ? (
            <span className="inline-flex items-center gap-1.5 text-[13px] text-[var(--ui-text)]">
              <BadgeCheck className="h-4 w-4 text-emerald-400" aria-hidden="true" /> Certified
            </span>
          ) : null}
          <Link
            to={`/charts/build?dataset=${Number(dataset.id)}`}
            className="rounded-lg bg-[var(--ui-inset)] px-3 py-1.5 text-[13px] text-[var(--ui-text)] hover:bg-[var(--ui-inset-hover)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
          >
            Build a chart
          </Link>
        </div>
      </div>
      {error && <p role="alert" className="mt-3 rounded-lg bg-red-500/10 px-3 py-1.5 text-xs text-red-300">{error}</p>}
    </div>
  );
}
